import { Platform } from 'react-native';

import {
  LAUNCH_DIAGNOSTICS_LOG_PREFIX,
  LAUNCH_DIAGNOSTICS_STORAGE_KEY,
  LAUNCH_PHASE,
  type LaunchPhase,
} from '../constants/launchDiagnostics.constants';

export interface LaunchPhaseRecord {
  phase: string;
  elapsedMs: number;
  at: string;
  detail: string | null;
}

let bootMs = Date.now();

/** Test-only: pin boot time so elapsedMs assertions are stable. */
export function resetLaunchBootMsForTests(anchorMs: number = Date.now()): void {
  bootMs = anchorMs;
}

export function getLaunchElapsedMs(): number {
  return Date.now() - bootMs;
}

export function isLaunchDiagnosticsEnabled(): boolean {
  return Platform.OS !== 'web';
}

export function formatLaunchDiagnosticMessage(phase: LaunchPhase, detail?: string): string {
  const elapsedMs = getLaunchElapsedMs();
  if (detail) {
    return `${LAUNCH_DIAGNOSTICS_LOG_PREFIX} phase=${phase} elapsedMs=${elapsedMs} detail=${detail}`;
  }
  return `${LAUNCH_DIAGNOSTICS_LOG_PREFIX} phase=${phase} elapsedMs=${elapsedMs}`;
}

/**
 * Log a launch milestone to device console and persist to AsyncStorage (await before native SDK calls).
 * If the app crashes during the next native call, the stored phase names the last step that completed.
 */
export async function logLaunchPhase(phase: LaunchPhase, detail?: string): Promise<void> {
  if (!isLaunchDiagnosticsEnabled()) return;

  console.warn(formatLaunchDiagnosticMessage(phase, detail));

  try {
    const AsyncStorage = require('@react-native-async-storage/async-storage').default;
    const record: LaunchPhaseRecord = {
      phase,
      elapsedMs: getLaunchElapsedMs(),
      at: new Date().toISOString(),
      detail: detail ?? null,
    };
    await AsyncStorage.setItem(LAUNCH_DIAGNOSTICS_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Diagnostics must never affect launch.
  }
}

export async function readLastLaunchPhase(): Promise<LaunchPhaseRecord | null> {
  if (!isLaunchDiagnosticsEnabled()) return null;

  try {
    const AsyncStorage = require('@react-native-async-storage/async-storage').default;
    const raw = await AsyncStorage.getItem(LAUNCH_DIAGNOSTICS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LaunchPhaseRecord;
    if (typeof parsed?.phase !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

/** On boot, log where the *previous* session stopped (useful after a crash). */
export async function reportPreviousLaunchPhaseOnBoot(): Promise<void> {
  if (!isLaunchDiagnosticsEnabled()) return;

  const previous = await readLastLaunchPhase();
  if (!previous) {
    await logLaunchPhase(LAUNCH_PHASE.PREVIOUS_PHASE_REPORT, 'none');
    return;
  }

  const summary = `prev=${previous.phase} prevElapsedMs=${previous.elapsedMs} prevAt=${previous.at}`;
  await logLaunchPhase(LAUNCH_PHASE.PREVIOUS_PHASE_REPORT, summary);
}
